//@ts-ignore
import { FormContext, iChoice, iControlProps } from "../types";

import { Container } from ".";
import React, { useCallback, useEffect, useRef, useState } from "react";
import EnchancedSelect, { components } from "react-select";
import { SelectV2, ComboboxV2, CheckboxV2 } from "@boi/react-components";
import { ChevronDownIcon, ChevronUpIcon } from "@boi/react-icons";
import solasTokens from "@boi/design-tokens/dist/web/boi/light/tokens.json";
import { evaluates_as_hiddenV2 } from "../conditional-logic";
import { replaceMergeTagsInString, stringHasValueTag } from "../helper";
import { SelectCustomStyles } from "./select/styles";
import { isMobile } from "../../theme/style-utils-coras-nua";

type SelectValue = {
    label: string;
    value?: string;
    options?: { label: string; value: string }[];
    customProps?: any;
};

export type Props = iControlProps & {
    choices: iChoice[];
    id?: string;
    cssClass?: string;
    menuIsOpen?: boolean;
    classNamePrefix?: string;
    isMulti?: boolean;
    closeMenuOnSelect?: boolean;
    placeholder?: string;
    error?: string | boolean;
    errorMessage?: string;
    showError?: boolean;
    searchableLimit: number;
    maxSelectableOptions: number;
    value: SelectValue;
    field_not_same_validation?: boolean;
    field_not_same_source?: string;
    onChange?: (value?: any) => void;
    onBlur?: (value?: any) => void;
    onFocus?: (value?: any) => void;
    onInputChange?: (value: string) => void;
    context?: FormContext;
    control?: any;
    singleValue?: any;
    isRequired?: boolean;
    customComponents?: any;
    isLoading?: boolean;
    isSearchable?: boolean;
    isDisabled?: boolean;
    isClearable?: boolean;
    inputValue?: string;
    openMenuOnClick?: boolean;
    styles?: any;
    ["aria-label"]?: string;
};

const convertToDashCase = (input: string): string =>
    input.trim().replace(/\s+/g, "-");

const DropdownIndicator = (props: any) => {
    return (
        components.DropdownIndicator && (
            <components.DropdownIndicator {...props} className="no-animation">
                {props.selectProps.menuIsOpen ?
                    <ChevronUpIcon
                        width={24}
                        height={24}
                        style={{ cursor: 'pointer', color: solasTokens.themeColor.core125 }}
                    /> :
                    <ChevronDownIcon
                        width={24}
                        height={24}
                        style={{ cursor: 'pointer', color: solasTokens.themeColor.core125 }}
                    />
                }
            </components.DropdownIndicator>
        )
    );
};

//@ts-ignore
const CustomValueContainer = ({ children, ...props }) => {
    const { getValue, options } = props;
    const selected = getValue();
    const allSelected = selected.length === options.length;

    let displayLabel;
    if (selected.length === 0) {
        displayLabel = props.selectProps.placeholder;
    } else if (selected.length === 1) {
        displayLabel = selected[0].label;
    } else if (allSelected) {
        displayLabel = "All";
    } else {
        displayLabel = `${selected.length} items selected`;
    }

    return (
        //@ts-ignore
        <components.ValueContainer {...props}>
            <div>{displayLabel}</div>
        </components.ValueContainer>
    );
};

const Select = (origProps: Props) => {
    const {
        choices,
        id,
        isMulti,
        placeholder,
        onChange,
        onBlur,
        onFocus,
        searchableLimit,
        value,
        field_not_same_validation,
        context,
        cssClass,
        isDisabled,
        ...props
    } = origProps;

    if (isMulti) {
        const [menuIsOpen, setMenuIsOpen] = useState(false);

        //@ts-ignore
        const CustomOption = ({ children, isSelected, innerRef, innerProps, selectProps, onChange, data, isFocused }) => {
            const [isHovered, setIsHovered] = useState(false);
            const selectedOptions = selectProps.value || [];
            const isDisabled = selectedOptions.length >= origProps.maxSelectableOptions && !isSelected;

            const handleCheckboxChange = () => {
                const newValue = isSelected
                    //@ts-ignore
                    ? selectedOptions.filter((option) => option.value !== data.value)
                    : [...selectedOptions, data];
                selectProps.onChange(newValue);
            };

            return (
                <div
                    onMouseEnter={() => {
                        if (!isDisabled) setIsHovered(true);
                    }}
                    onMouseLeave={() => setIsHovered(false)}
                    style={{ cursor: isDisabled ? 'not-allowed' : 'pointer', color: solasTokens.themeColor.base400, backgroundColor: isHovered ? solasTokens.themeColor.base150 : solasTokens.themeColor.base100 }}>
                    {selectProps.options.indexOf(data) !== 0 && (
                        <hr
                            style={{
                                margin: 0,
                                border: 'none',
                                borderTop: `1px solid ${solasTokens.themeColor.base200}`
                            }}
                        />
                    )}
                    <div
                        ref={innerRef}
                        {...innerProps}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 12px',
                            color: isDisabled ? solasTokens.themeColor.base400 : 'red',
                            pointerEvents: isDisabled ? 'none' : 'auto',
                            opacity: isDisabled ? 0.6 : 1,
                            outlineColor: solasTokens.themeColor.base200,
                        }}
                    >
                        <CheckboxV2
                            labelProps={{
                                label: children
                            }}
                            isSelected={isSelected}
                            onChange={handleCheckboxChange}
                            style={{ marginRight: '10px' }}
                        />
                    </div>
                </div>
            );
        };

        const checkMenuPlacement = isMobile() ? "bottom" : "auto";

        const [isDisabled, setIsDisabled] = useState<boolean>(
            origProps.isDisabled || false
        );

        let field_not_same_source;
        try {
            field_not_same_source = JSON.parse(`${props.field_not_same_source}`);
            field_not_same_source = Array.isArray(field_not_same_source)
                ? field_not_same_source
                : [field_not_same_source];
        } catch (error) {
            field_not_same_source = [];
        }
        const excludeValues =
            (context &&
                field_not_same_validation &&
                field_not_same_source.length &&
                field_not_same_source.map(
                    (id: string) => (context.getValue(`input_${id}`) || {}).value
                )) ||
            [];
        let optionsLength = 0;
        const options = choices.reduce(
            (o: {
                label: string;
                value?: string;
                options?: { label: string, value: string }[];
                customProps?: any;
            }[], choice: iChoice) => {
                let hidden = false;
                if (context && choice.conditionalLogic) {
                    hidden = evaluates_as_hiddenV2(
                        choice.conditionalLogic,
                        context.state
                    );
                }
                if (!hidden) {
                    let label = choice.text;

                    if (choice.value !== undefined && !excludeValues.includes(choice.value)) {
                        if (!stringHasValueTag(choice.value)) {
                            label = replaceMergeTagsInString(
                                choice.text,
                                context ? context.getValues() : {}
                            );
                        }
                        optionsLength++;

                        return [
                            ...o,
                            {
                                label: label,
                                value: choice.value,
                                customProps: choice.customProps
                            }
                        ];
                    } else if (choice.options !== undefined && choice.options.length) {
                        const subOptions: { label: string, value: string }[] = [];
                        choice.options.forEach(subOption => {
                            subOptions.push({
                                label: subOption.text,
                                value: subOption.value
                            });
                            optionsLength++;
                        });

                        return [
                            ...o,
                            {
                                label: label,
                                options: subOptions
                            }
                        ];
                    } else {
                        return o;
                    }
                } else {
                    return o;
                }
            },
            []
        );

        /**
         * Ensure values exist within the drop down
         */
        let selectedValue: SelectValue | SelectValue[] | null;
        if (isMulti) {
            const requestedValues = Array.isArray(value)
                ? value.map(v => v.value)
                : [value];
            selectedValue = options.filter(v => requestedValues.includes(v.value));
            selectedValue =
                selectedValue !== null && selectedValue.length
                    ? selectedValue
                    : null;
        } else if (origProps.isSearchable && !options.length) {
            selectedValue = value;
        } else {
            const requestedValue =
                value && typeof value === "object" ? value.value : value;
            selectedValue = null;
            for (const option of options) {
                if ((option.value != null) && option.value === requestedValue) {
                    selectedValue = option;
                    //@ts-ignore
                } else if (option.options && option.options.length) {
                    //@ts-ignore
                    for (const subOption of option.options) {
                        if (subOption.value === requestedValue) {
                            selectedValue = subOption;
                            break;
                        }
                    }
                }
                if (selectedValue) {
                    break;
                }
            }
        }

        const unsetValueEffect = (value: SelectValue) => () => {
            if (value && selectedValue === null && props.name) {
                context?.unsetValue(props.name);
            }
        };

        const disableSelect = () => {
            //@ts-ignore
            if (props.isRequired && options.length === 1 && !options[0].options) {
                setIsDisabled(true);
                if (!selectedValue) {
                    selectedValue = isMulti ? options : options[0];
                    onChange && onChange(selectedValue);
                }
            }
        };

        useEffect(disableSelect, [choices]);
        useEffect(unsetValueEffect(value), [selectedValue]);

        const dropdownRef = useRef<HTMLDivElement>(null);
        const toggleMenu = useCallback(() => {
            setMenuIsOpen((prev) => !prev);
        }, []);

        const closeMenu = useCallback(() => {
            setMenuIsOpen(false);
        }, []);

        useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                if (dropdownRef.current) {
                    if (!dropdownRef.current.contains(event.target as Node)) {
                        closeMenu();
                    } else {
                        toggleMenu();
                    }
                }
            };

            document.addEventListener("mousedown", handleClickOutside);

            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }, [closeMenu]);

        return (
            <Container {...{ ...props, id, cssClass }}>
                <div ref={dropdownRef} className="react-select-container">
                    <EnchancedSelect
                        isSearchable={false}
                        defaultMenuIsOpen={origProps.menuIsOpen}
                        menuIsOpen={menuIsOpen}
                        onMenuOpen={() => setMenuIsOpen(true)}
                        onMenuClose={() => setMenuIsOpen(false)}
                        value={selectedValue}
                        closeMenuOnSelect={origProps.closeMenuOnSelect}
                        hideSelectedOptions={false}
                        blurInputOnSelect={false}
                        maxMenuHeight={410}
                        tabIndex={origProps.tabIndex}
                        isMulti={isMulti}
                        menuShouldScrollIntoView={true}
                        menuPlacement={checkMenuPlacement}
                        placeholder={
                            placeholder
                                ? placeholder
                                : optionsLength > searchableLimit
                                    ? "Type to search"
                                    : "Please select"
                        }
                        classNamePrefix={origProps.classNamePrefix}
                        inputId={id}
                        className="react-select-container"
                        options={options}
                        onChange={(e: any) => onChange && onChange(e)}
                        onBlur={(e: any) => onBlur && onBlur(e)}
                        onFocus={(e: any) => onFocus && onFocus(e)}
                        onInputChange={origProps.onInputChange}
                        isLoading={origProps.isLoading}
                        isDisabled={isDisabled}
                        inputValue={origProps.inputValue}
                        isClearable={origProps.isClearable}
                        openMenuOnClick={origProps.openMenuOnClick}
                        aria-label={props["aria-label"] ?? ""}
                        components={{
                            ...(origProps.control && { Control: origProps.control }),
                            ...(origProps.singleValue && { SingleValue: origProps.singleValue }),
                            ...origProps.customComponents,
                            Option: CustomOption,
                            MultiValueRemove: () => null,
                            ValueContainer: CustomValueContainer,
                            ClearIndicator: () => null,
                            DropdownIndicator
                        }}
                        styles={{
                            ...SelectCustomStyles,
                            ...props.styles,
                            menu: (styles) => ({
                                ...styles,
                                border: '1px solid #bbb',
                                borderRadius: 8,
                                boxShadow: 'rgba(0, 0, 0, 0.24) 0px 2px 2px',
                                overflow: 'hidden',
                                zIndex: 5
                            }),
                            option: (styles, { isDisabled }) => {
                                return {
                                    ...styles,
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',

                                    ':hover': {
                                        ...styles[':hover'],
                                        backgroundColor: "red"
                                    }
                                };
                            },
                            control: (styles, { isFocused }) => {
                                return {
                                    ...styles,
                                    borderWidth: 2,
                                    paddingTop: 12,
                                    paddingBottom: 12,
                                    paddingLeft: 16,
                                    paddingRight: 16,
                                    fontWeight: 600,
                                    borderColor: isFocused || menuIsOpen ? solasTokens.themeColor.core125 : solasTokens.themeColor.base400,
                                    borderRadius: 8,
                                    position: 'relative',
                                    cursor: 'pointer',

                                    ':hover': {
                                        ...styles[':hover'],
                                        borderColor: solasTokens.themeColor.core125
                                    }
                                }
                            },
                        }}
                    />
                </div>
            </Container>
        );






    } else {
        const [options, setOptions] = useState<
            { id: string; title: string; value: any; customProps: any }[]
        >([]);
        const [optionsSize, setOptionsSize] = useState<number>(0);

        let optionsLength = 0;

        let field_not_same_source;
        try {
            field_not_same_source = JSON.parse(`${props.field_not_same_source}`);
            field_not_same_source = Array.isArray(field_not_same_source)
                ? field_not_same_source
                : [field_not_same_source];
        } catch (error) {
            field_not_same_source = [];
        }
        const excludeValues =
            (context &&
                field_not_same_validation &&
                field_not_same_source.length &&
                field_not_same_source.map(
                    (id: string) => (context.getValue(`input_${id}`) || {}).value
                )) ||
            [];

        const isDisabledOrReadOnly =
            isDisabled || options.length === 0 || options.length === 1 || cssClass?.includes("readonly");

        if (options.length === 1 && options[0].value !== value) {
            onChange && onChange(options[0].value);
        }

        const accessibilitySettings: { [key: string]: string } = {};
        props.accessibilitySettings &&
            props.accessibilitySettings.forEach(setting => {
                if (setting.isShown)
                    accessibilitySettings[setting.name] = setting.value || "";
            });

        useEffect(() => {
            const pureOptions = choices.reduce(
                (
                    o: {
                        label: string;
                        value?: string;
                        options?: { label: string; value: string }[];
                        customProps?: any;
                    }[],
                    choice: iChoice
                ) => {
                    let hidden = false;
                    if (context && choice.conditionalLogic) {
                        hidden = evaluates_as_hiddenV2(
                            choice.conditionalLogic,
                            context.state
                        );
                    }
                    if (!hidden) {
                        let label = choice.text;

                        if (
                            choice.value !== undefined &&
                            !excludeValues.includes(choice.value)
                        ) {
                            if (!stringHasValueTag(choice.value)) {
                                label = replaceMergeTagsInString(
                                    choice.text,
                                    context ? context.getValues() : {}
                                );
                            }
                            optionsLength++;

                            // Remove duplications
                            if (o.filter((item) => item.label === label).length > 0) {
                                return o;
                            } else {
                                return [
                                    ...o,
                                    {
                                        label: label,
                                        value: choice.value,
                                        customProps: choice.customProps
                                    }
                                ];
                            }

                        } else if (
                            choice.options !== undefined &&
                            choice.options.length
                        ) {
                            const subOptions: {
                                label: string;
                                value: string;
                                customProps?: any;
                            }[] = [];
                            choice.options.forEach(subOption => {
                                subOptions.push({
                                    label: subOption.text,
                                    value: subOption.value
                                });
                                optionsLength++;
                            });

                            return [...o, ...subOptions];
                        } else {
                            return o;
                        }
                    } else {
                        return o;
                    }
                },
                []
            ).map(
                ({ label, value = "", customProps }) => {
                    return {
                        id: convertToDashCase(label),
                        title: label,
                        value,
                        customProps
                    };
                }
            )
            setOptionsSize(optionsLength)
            setOptions(pureOptions);
        }, [choices, context])

        const isSearchable =
            origProps.isSearchable || optionsSize > origProps.searchableLimit;

        return (
            <>
                <Container {...{ ...props, id, cssClass }}>
                    {isSearchable ? (
                        <ComboboxV2
                            isDisabled={isDisabled}
                            selectedKey={value}
                            items={options}
                            aria-label={accessibilitySettings["aria-label"]}
                            onSelectionChange={newValue => {
                                if (newValue) {
                                    onChange && onChange(newValue);
                                }
                            }}
                            onInputChange={origProps.onInputChange}
                            onBlur={(e: any) => onBlur && onBlur(e)}
                            onFocus={(e: any) => onFocus && onFocus(e)}
                            placeholder={
                                placeholder
                                    ? placeholder
                                    : optionsLength > searchableLimit
                                        ? "Type to search"
                                        : "Please select"
                            }
                            {...accessibilitySettings}
                        />
                    ) : (
                        <SelectV2
                            isDisabled={isDisabledOrReadOnly}
                            selectedKey={value.value ? value.value : value}
                            items={options}
                            aria-label={accessibilitySettings["aria-label"]}
                            onSelectionChange={newValue => {
                                if (newValue) {
                                    onChange && onChange(newValue);
                                }
                            }}
                            onBlur={(e: any) => onBlur && onBlur(e)}
                            onFocus={(e: any) => onFocus && onFocus(e)}
                            placeholder={
                                placeholder ? placeholder : "Please select"
                            }
                            {...accessibilitySettings}
                        />
                    )}
                </Container>
            </>
        );

    }

};
Select.defaultProps = {
    choices: [],
    menuIsOpen: false,
    isMulti: false,
    placeholder: "Choose an option ...",
    searchableLimit: 25,
    maxSelectableOptions: 4,
    value: "",
    error: "",
    isLoading: false
};
export default Select;
